import React, { useState, useEffect, useRef } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import axios from "axios"
import { MdMic, MdPlayArrow, MdStop, MdReplay } from 'react-icons/md';
import app from '../firebase.js';

const VoiceTranscribe = () => {
    const [isRecording, setIsRecording] = useState(false);
    const [isStopped, setIsStopped] = useState(false);
    const [audioFile, setAudioFile] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [transcribedText, setTranscribedText] = useState('');
    const [isCopySuccess, setIsCopySuccess] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [audioChunks, setAudioChunks] = useState([]);
    const [audioBlob, setAudioBlob] = useState(null);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [isBreaking, setIsBreaking] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [timerId, setTimerId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [sourceLanguage, setSourceLanguage] = useState("en");
    const [translationLanguages, setTranslationLanguages] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [filename, setFilename] = useState("");

    let mediaRecorder;
    let audioContext;
    let analyser;

    const mediaRecorderRef = useRef(null);
    const audioRef = useRef(null);
    let resumeAt = 0;

    useEffect(() => {
        const fetchTranslationLanguages = async () => {
            const options = {
                method: 'GET',
                url: 'https://text-translator2.p.rapidapi.com/getLanguages',
                headers: {
                    'X-RapidAPI-Key': 'google-translate-api key from rapid api',
                    'X-RapidAPI-Host': 'text-translator2.p.rapidapi.com'
                }
            };

            try {
                const response = await axios.request(options);
                setTranslationLanguages(response.data.data.languages);
            } catch (error) {
                console.error('Error fetching translation languages', error);
            }
        };

        fetchTranslationLanguages();
    }, []);


    const handleRecord = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const microphone = audioContext.createMediaStreamSource(stream);
            analyser = audioContext.createAnalyser();
            const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

            analyser.smoothingTimeConstant = 0.3;
            analyser.fftSize = 1024;

            microphone.connect(analyser);
            analyser.connect(scriptProcessor);
            scriptProcessor.connect(audioContext.destination);

            let chunks = [];

            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const fileName = `audio_${new Date().getTime()}.wav`;
                const blob = new Blob(chunks, { type: 'audio/*' });
                setAudioBlob(blob);
                setAudioFile(URL.createObjectURL(blob));
                setAudioChunks(chunks);
                setFilename(fileName)
                setIsRecording(false);
                setIsBreaking(false);
                resumeAt = 0;
            };


            mediaRecorderRef.current.onresume = () => {
                if (isBreaking) {
                    resumeAt += Date.now() - mediaRecorderRef.current.pauseStart;
                    setIsBreaking(false);
                }
            };

            mediaRecorderRef.current.onpause = () => {
                if (!isBreaking) {
                    mediaRecorderRef.current.pauseStart = Date.now();
                }
            };

            scriptProcessor.onaudioprocess = (event) => {
                const dataArray = event.inputBuffer.getChannelData(0);
            };

            mediaRecorderRef.current.start();

            audioContext.resume().then(() => {
                setIsRecording(true);
                const startTime = Date.now() - resumeAt;
                const updateRecordingTime = () => {
                    setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
                };

                const timerId = setInterval(updateRecordingTime, 1000);
                setTimerId(timerId);

                updateRecordingTime();

                const stopRecording = () => {
                    clearInterval(timerId);
                    mediaRecorderRef.current.stop();
                    stream.getTracks().forEach((track) => track.stop());
                    resumeAt = 0;
                };

                setTimeout(stopRecording, 3600000);
            });
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };

    const handleStop = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            if (analyser) {
                analyser.disconnect();
            }
            if (audioContext) {
                audioContext.close();
            }
        }
        setIsRecording(false);
        setIsStopped(true);
        setIsModalOpen(false);
        clearInterval(timerId);
        setRecordingTime(0);

        const fileName = `audio_${new Date().getTime()}.wav`;
        setFilename(fileName);
    };

    const clearAudio = () => {
        setAudioFile(null);
        setAudioChunks([]);
        setIsRecording(false);
        setRecordingTime(0);
        audioRef.current = null;
    }

    const handleModalToggle = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            if (analyser) {
                analyser.disconnect();
            }
            if (audioContext) {
                audioContext.close();
            }
        }
        setIsRecording(false);
        setAudioChunks([]);
        setAudioFile(null);
        clearInterval(timerId);
        setRecordingTime(0);
        setIsModalOpen(!isModalOpen);
    };

    const handlePlayAudio = () => {
        if (!audioRef.current) {
            audioRef.current = new Audio(audioFile);
            audioRef.current.onended = () => setIsPlayingAudio(false);
        }

        if (!isPlayingAudio) {
            audioRef.current.play();
        } else {
            audioRef.current.pause();
        }

        setIsPlayingAudio(!isPlayingAudio);
    };


    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            if (analyser) {
                analyser.disconnect();
            }
            if (audioContext) {
                audioContext.close();
            }
            resumeAt = 0;
        };
    }, []);

    const handleTranscribe = async () => {
        console.log("fileName", filename);
        setIsTranslating(true);
        try {
            setIsLoading(true);
            setIsUploading(true);

            const storage = getStorage(app);
            const fileName = new Date().getTime() + filename;
            const storageRef = ref(storage, fileName);
            const metadata = { contentType: 'audio/*' };

            const uploadTask = uploadBytesResumable(storageRef, audioFile, metadata);

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);

                    console.log(`Upload is ${progress}% done`);
                },
                (error) => {
                    console.error('Error uploading audio file to Firebase', error);
                    setIsUploading(false);
                },
                async () => {
                    const audioUrl = await getDownloadURL(uploadTask.snapshot.ref);

                    const response = await axios.post('http://localhost:8080/api/v1/audio/uploadAudio', {
                        fileUrl: audioUrl,
                        fileName: fileName,
                    });

                    await setTranscribedText(response.data.transcription);
                    setIsUploading(false);
                    setUploadProgress(0);
                }

            );
            setIsLoading(false)
            setIsTranslating(false);
        } catch (error) {
            console.error('Error handling transcription', error);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(transcribedText);
        setIsCopySuccess(true);
        setTimeout(() => {
            setIsCopySuccess(false);
        }, 3000);
    };

    const handleSaveAsPDF = async () => {
        // Implement saving as PDF logic here
        // Send the URL to the backend and display in 'My Files'
    };

    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
    };

    const handleTranslate = async () => {
        try {
            if (sourceLanguage !== selectedLanguage) {
                const encodedParams = new URLSearchParams();
                encodedParams.set('source_language', sourceLanguage);
                encodedParams.set('target_language', selectedLanguage);
                encodedParams.set('text', transcribedText);

                const translationOptions = {
                    method: 'POST',
                    url: 'https://text-translator2.p.rapidapi.com/translate',
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                        'X-RapidAPI-Key': 'text-translator2-api key from rapid api',
                        'X-RapidAPI-Host': 'text-translator2.p.rapidapi.com'
                    },
                    data: encodedParams,
                };

                const translationResponse = await axios.request(translationOptions);

                const translatedText = translationResponse.data.data.translatedText;

                setTranscribedText(translatedText);
                setSourceLanguage(selectedLanguage);
            } else {
                const encodedParams = new URLSearchParams();
                encodedParams.set('target_language', selectedLanguage);
                encodedParams.set('text', transcribedText);

                const translationOptions = {
                    method: 'POST',
                    url: 'https://text-translator2.p.rapidapi.com/translate',
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                        'X-RapidAPI-Key': 'text-translator2-api key from rapid api',
                        'X-RapidAPI-Host': 'text-translator2.p.rapidapi.com'
                    },
                    data: encodedParams,
                };

                const translationResponse = await axios.request(translationOptions);

                const translatedText = translationResponse.data.data.translatedText;

                setTranscribedText(translatedText);
                setSourceLanguage(selectedLanguage);
            }
        } catch (error) {
            console.error('Error translating text', error);
        }
    };

    useEffect(() => {
        let animationInterval;
        if (isRecording) {
            animationInterval = setInterval(() => {
            }, 500);
        }
        return () => {
            clearInterval(animationInterval);
        };
    }, [isRecording]);



    return (
        <div className="p-8 text-center bg-gradient w-full font-primary mb-10">
            <div className="absolute top-0 left-[55%] overlay-glow2"></div>
            <div className='flex flex-col justify-center items-center'>
                <button
                    className="primary-color text-white mb-5 inline-block dark:text-neutral-200 text-xl text-light text-center font-bold my-3 p-4 rounded-md"
                    onClick={handleModalToggle}
                    disabled={isPlaying || audioFile}
                >
                    Record Voice to Transcribe
                </button>
                {audioFile && (
                    <div className="mb-8">
                        <div
                            className="text-4xl text-[#ad53e5] cursor-pointer"
                            onClick={handlePlayAudio}
                        >
                            {isPlayingAudio ? '||' : <MdPlayArrow />}
                        </div>
                    </div>
                )}

                {isUploading && (
                    <div className="text-light mb-4 disabled:cursor-wait">
                        Uploading... {uploadProgress.toFixed(2)}% complete
                    </div>
                )}

                <div className='flex flex-row gap-2'>
                    <button
                        className="text-white p-3 rounded-md mb-8 primary-color inline-block dark:text-neutral-200 text-lg text-light text-center font-semibold my-3"
                        onClick={clearAudio}
                        disabled={!audioFile}
                    >
                        Clear Audio
                    </button>
                    <button
                        className="text-white p-3 rounded-md mb-8 primary-color inline-block dark:text-neutral-200 text-lg font-semibold text-light text-center my-3"
                        onClick={handleTranscribe}
                        disabled={!audioFile || isRecording || isPlaying}
                    >
                        Transcribe to Text
                    </button>
                </div>

                {(uploadProgress === 100 || isLoading) && (
                    <div className="text-white mb-4">
                        Transcribing...
                    </div>
                )}
            </div>

            {/* Recording Modal */}
            {isModalOpen && (
                <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-gradient2 bg-opacity-75 modal-overlay">
                    <div className="p-6 md:p-12 lg:p-24 rounded-md md:max-w-md lg:max-w-3xl text-center relative bg-gradient">
                        <MdMic className="text-6xl text-white mb-8 md:mb-12 mx-auto animate-pulse" />
                        <div className="flex flex-wrap md:flex-row justify-center items-end md:items-center gap-4">
                            <button
                                className="bg-green-500 text-white px-4 py-2 rounded-md"
                                onClick={handleRecord}
                                disabled={isRecording}
                            >
                                Start
                            </button>
                            <button
                                className="bg-red-500 text-white px-4 py-2 rounded-md"
                                onClick={handleStop}
                                disabled={!isRecording}
                            >
                                Stop
                            </button>
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded-md"
                                onClick={handleModalToggle}
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="text-white mt-4">
                            {isRecording && `Recording... ${recordingTime}s`}
                        </div>
                    </div>
                </div>
            )}


            {/* Transcribe Section */}
            <div className="bg-gray-100 p-8 my-8 text-left rounded-md w-[85%] mx-auto">
                <div className="mb-4">
                    <label className="mr-2 font-semibold">Transcribed Text:</label>
                    <div
                        className={`border border-[#ad53e5] p-4 rounded-md h-40 overflow-auto ${isCopySuccess ? 'bg-green-100' : ''
                            }`}
                    >
                        {transcribedText}
                    </div>
                    {transcribedText && (
                        <div className='flex flex-col md:flex-row gap-2 mt-4'>
                            <button
                                onClick={handleCopyText}
                                className={`text-white bg-blue-500 px-6 py-3 rounded-md ${isCopySuccess ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                disabled={isCopySuccess || isLoading}
                            >
                                {isCopySuccess ? 'Copied!' : 'Copy Text'}
                            </button>
                            <button
                                onClick={handleSaveAsPDF}
                                className={`text-white bg-green-500 px-6 py-3 rounded-md mt-4 md:mt-0 ${isCopySuccess ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                disabled={!transcribedText || isRecording || isPlaying || isLoading}
                            >
                                {isUploading ? 'Saved!' : 'Save as Text'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="w-[100%] flex flex-wrap flex-col gap-4 md:gap-1 md:flex-row items-center justify-center mb-8">
                {/* <label className="mr-2 hidden">Select Language:</label> */}
                <select
                    value={selectedLanguage}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md max-sm:px-2 max-sm:py-1"
                    disabled={isUploading || !transcribedText || isTranslating}
                >
                    {/* {translationLanguages.map((language) => (
                        <option key={language.code} value={language.code}>
                            {language.name}
                        </option>
                    ))} */}

                    {translationLanguages.map((languageObj) => {
                        const [code, name] = Object.entries(languageObj)[0];
                        return (
                            <option key={code} value={code}>
                                {name}
                            </option>
                        );
                    })}
                </select>
                <button
                    className="bg-gradient2 font-semibold text-white px-6 py-3 ml-2 max-sm:mt-3 rounded-md"
                    onClick={handleTranslate}
                    disabled={!transcribedText || isUploading || isTranslating}
                >
                    {isTranslating ? 'Translating...' : 'Translate'}
                </button>
            </div>

            {isRecording && <div className="text-gray-500">Recording...</div>}
        </div>
    );
};

export default VoiceTranscribe;
